/**
 * Captures the most recent uncaught JS error or unhandled promise rejection,
 * so a bug report can attach it automatically. Observes only — never affects
 * error propagation. Wired up once from ActionTrailListener's mount effect.
 */

const MAX_STACK_LENGTH = 2000;

export interface LastError {
  message: string;
  stack?: string;
  at: string;
  /** Ids an auto-report attaches to the error it caught (e.g. child, acting
   *  staff, location code) — never set by the global capture. */
  context?: Record<string, string | null>;
}

let lastError: LastError | null = null;
let initialized = false;

function capture(message: string, stack?: string): void {
  lastError = {
    message,
    stack: stack ? stack.slice(0, MAX_STACK_LENGTH) : undefined,
    at: new Date().toISOString(),
  };
}

export function getLastError(): LastError | null {
  return lastError;
}

export function initLastErrorCapture(): void {
  if (initialized) return;
  initialized = true;

  window.addEventListener('error', (event: ErrorEvent) => {
    capture(event.message, event.error?.stack);
  });

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const reason = event.reason;
    const message = reason instanceof Error ? reason.message : String(reason);
    const stack = reason instanceof Error ? reason.stack : undefined;
    capture(message, stack);
  });
}
